import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import { Column, FilterFieldCollection, FilterOperation } from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useTemplateApi from "hooks/api/private/useTemplateApi";
import useSnackbar from "hooks/useSnackbar";

import { TemplateListItem } from "types/api/template";

export type TemplateListHook = {
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => void;
    navigateToNewTemplate: () => void;
    alertConfirmHook: AlertConfirmHookType;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    dataTableHook: DataTableHook<TemplateListItem>;
};

function useTemplateList(): TemplateListHook {
    const { getAllTemplate, deleteTemplate } = useTemplateApi();

    const navigate = useNavigate();

    const navigateToEditTemplate = React.useCallback(
        (row: TemplateListItem) => {
            navigate(makeUrl(":templateId/modifica", { templateId: row.id }));
        },
        [navigate]
    );

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        []
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "template-list-search",
        memoizedFieldsForSearch
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.name = value;

                return newForm;
            }),
        [setFiltersForm]
    );

    const { filters } = filterSearchHook;

    const [triggerReloadTable, setTriggerReloadTable] = React.useState<boolean>(false);

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Template",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteTemplateButton = React.useCallback(
        (row: TemplateListItem) => {
            openConfirmAlert(
                <>
                    <p>Sei sicuro di voler eliminare il Template?</p>
                    <p>potrebbe essere stato usato per un prodotto</p>
                </>,
                () => {
                    const [apiCall] = deleteTemplate({ id: row.id });

                    apiCall
                        .then(() => {
                            setTriggerReloadTable(true);
                            enqueueSuccessSnackbar("Template eliminato con successo");
                        })
                        .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del Template"));
                }
            );
        },
        [
            setTriggerReloadTable,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            openConfirmAlert,
            deleteTemplate,
        ]
    );

    const columns: Array<Column<TemplateListItem>> = React.useMemo(
        (): Array<Column<TemplateListItem>> => [
            {
                id: "name",
                header: "Nome",
                orderable: true,
                render: (row: TemplateListItem) => row.name,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: TemplateListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={navigateToEditTemplate}
                        onDelete={deleteTemplateButton}
                    />
                ),
            },
        ],
        [navigateToEditTemplate, deleteTemplateButton]
    );

    const dataTableHook = useDataTableWithMemory<TemplateListItem>({
        apiCall: getAllTemplate,
        getElements: calculateRows,
        columns: columns,
        size: 20,
        filters: filters,
        key: "template-list",
    });

    const { loadTableData } = dataTableHook;

    React.useEffect(() => {
        if (triggerReloadTable) {
            loadTableData();

            setTriggerReloadTable(false);
        }
    }, [loadTableData, triggerReloadTable]);

    const navigateToNewTemplate = React.useCallback(() => {
        navigate("/templates/nuovo");
    }, [navigate]);

    return React.useMemo(
        (): TemplateListHook => ({
            navigateToNewTemplate,
            onSearchFilterChange,
            alertConfirmHook,
            dataTableHook,
            filterSearchHook,
        }),
        [
            navigateToNewTemplate,
            onSearchFilterChange,
            alertConfirmHook,
            filterSearchHook,
            dataTableHook,
        ]
    );
}

export default useTemplateList;
